import config from "../config/config";
import { resolveMediaUrl } from "./resolveMediaUrl";

function backendOrigin() {
  const base = String(config.BASE_URL || "").trim();
  if (/^https?:\/\//i.test(base)) {
    return base.replace(/\/api\/?$/, "");
  }
  return "";
}

/** Prefer same-origin /assets paths so CRA dev proxy can serve files. */
function toFetchableUrl(resolved) {
  if (!resolved) return "";
  try {
    const fileUrl = new URL(resolved, window.location.origin);

    if (fileUrl.origin === window.location.origin) {
      return `${fileUrl.pathname}${fileUrl.search}`;
    }

    if (fileUrl.pathname.startsWith("/assets/")) {
      return `${fileUrl.pathname}${fileUrl.search}`;
    }

    const backend = backendOrigin();
    if (backend) {
      const apiOrigin = new URL(backend);
      if (
        fileUrl.origin === apiOrigin.origin &&
        fileUrl.pathname.startsWith("/assets/")
      ) {
        return `${fileUrl.pathname}${fileUrl.search}`;
      }
    }
  } catch {
    // fall through
  }
  return resolved;
}

function isSameOriginUrl(fetchUrl) {
  if (!fetchUrl || fetchUrl.startsWith("/")) return true;
  try {
    return new URL(fetchUrl, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

function looksLikeImage(url) {
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(String(url || ""));
}

export function filenameFromMediaUrl(url) {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const base = decodeURIComponent(pathname.split("/").pop() || "");
    return base || "download";
  } catch {
    return "download";
  }
}

function triggerBlobDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

async function fetchBlobFromUrl(fetchUrl) {
  const sameOrigin = isSameOriginUrl(fetchUrl);
  const res = await fetch(fetchUrl, {
    credentials: sameOrigin ? "include" : "omit",
    mode: "cors",
  });
  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }
  return res.blob();
}

async function fetchImageViaCanvas(imageUrl) {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = imageUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(img, 0, 0);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error("Export failed"))),
      "image/jpeg",
      0.92,
    );
  });
  return blob;
}

/**
 * Fetch a stored media URL and save it as a file download.
 */
export async function downloadMediaUrl(url, preferredFilename) {
  const resolved = resolveMediaUrl(url);
  if (!resolved) throw new Error("No file to download");

  const fetchUrl = toFetchableUrl(resolved);
  const filename = preferredFilename || filenameFromMediaUrl(resolved);
  const imageCandidate = looksLikeImage(resolved) || looksLikeImage(filename);

  try {
    const blob = await fetchBlobFromUrl(fetchUrl);
    triggerBlobDownload(blob, filename);
    return;
  } catch {
    // Cross-origin storage (e.g. Supabase) can reject credentialed fetch — try canvas for images.
  }

  if (imageCandidate) {
    const blob = await fetchImageViaCanvas(resolved);
    triggerBlobDownload(blob, filename);
    return;
  }

  throw new Error("Download failed");
}
